import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const StrategyToolbar = ({ 
  strategy, 
  onSave, 
  onLoad, 
  onNew, 
  onExport, 
  onValidate,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  validationErrors
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [strategyName, setStrategyName] = useState(strategy?.name || '');
  const [strategyDescription, setStrategyDescription] = useState(strategy?.description || '');

  const handleSave = async () => {
    if (!strategyName?.trim()) {
      setShowSaveDialog(true);
      return;
    }

    setIsSaving(true);
    try {
      await onSave({
        ...strategy,
        name: strategyName,
        description: strategyDescription,
        lastModified: new Date()?.toISOString()
      });
      setShowSaveDialog(false);
    } catch (error) {
      console.error('Error saving strategy:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuickSave = () => {
    if (strategy?.name) {
      handleSave();
    } else {
      setShowSaveDialog(true);
    }
  };

  const exportOptions = [
    { value: 'json', label: 'JSON Format' },
    { value: 'python', label: 'Python Code' },
    { value: 'pine', label: 'Pine Script' },
    { value: 'mql', label: 'MQL4/5' }
  ];

  const templateOptions = [
    { value: 'blank', label: 'Blank Strategy' },
    { value: 'ma-crossover', label: 'Moving Average Crossover' },
    { value: 'mean-reversion', label: 'Mean Reversion' },
    { value: 'momentum', label: 'Momentum Strategy' },
    { value: 'pairs-trading', label: 'Pairs Trading' },
    { value: 'breakout', label: 'Breakout Strategy' }
  ];

  return (
    <>
      <div className="bg-card border-b border-border px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Left Section - File Operations */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onNew}
              iconName="Plus"
              iconPosition="left"
            >
              New
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onLoad}
              iconName="FolderOpen"
              iconPosition="left"
            >
              Open
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleQuickSave}
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
            >
              Save
            </Button>

            <div className="w-px h-6 bg-border mx-2" />

            {/* Undo/Redo */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onUndo}
              disabled={!canUndo}
              iconName="Undo"
            />
            
            <Button
              variant="ghost"
              size="sm"
              onClick={onRedo}
              disabled={!canRedo}
              iconName="Redo"
            />

            <div className="w-px h-6 bg-border mx-2" />

            {/* Validation */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onValidate}
              iconName="CheckCircle"
              iconPosition="left"
              className={validationErrors?.length > 0 ? 'text-error' : 'text-success'}
            >
              Validate
              {validationErrors?.length > 0 && (
                <span className="ml-1 bg-error text-error-foreground text-xs rounded-full px-1.5 py-0.5">
                  {validationErrors?.length}
                </span>
              )}
            </Button>
          </div>

          {/* Center Section - Strategy Info */}
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-sm font-medium text-foreground">
                {strategy?.name || 'Untitled Strategy'}
              </div>
              <div className="text-xs text-muted-foreground">
                {strategy?.components?.length || 0} components • {strategy?.connections?.length || 0} connections
              </div>
            </div>
          </div>

          {/* Right Section - Export & Templates */}
          <div className="flex items-center gap-2">
            <Select
              options={templateOptions}
              placeholder="Load Template"
              onChange={(template) => onLoad(template)}
              className="w-40"
            />

            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport('json')}
              iconName="Download"
              iconPosition="left"
            >
              Export
            </Button>

            <Button
              variant="default"
              size="sm"
              onClick={() => {/* Deploy strategy */}}
              iconName="Rocket"
              iconPosition="left"
            >
              Deploy
            </Button>
          </div>
        </div>

        {/* Validation Errors */}
        {validationErrors && validationErrors?.length > 0 && (
          <div className="mt-3 p-3 bg-error/10 border border-error/20 rounded-md">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="AlertTriangle" size={16} className="text-error" />
              <span className="text-sm font-medium text-error">Strategy Validation Errors</span>
            </div>
            <ul className="text-xs text-error space-y-1">
              {validationErrors?.map((error, index) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-error/60">•</span>
                  <span>{error}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {/* Save Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 w-96 shadow-elevated">
            <h3 className="text-lg font-semibold text-foreground mb-4">Save Strategy</h3>
            
            <div className="space-y-4">
              <Input
                label="Strategy Name"
                value={strategyName}
                onChange={(e) => setStrategyName(e?.target?.value)}
                placeholder="Enter strategy name"
                required
              />
              
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Description
                </label>
                <textarea
                  value={strategyDescription}
                  onChange={(e) => setStrategyDescription(e?.target?.value)}
                  placeholder="Describe your strategy..."
                  className="w-full p-2 bg-background border border-border rounded-md text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              <div className="bg-muted/50 rounded-md p-3">
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="flex justify-between">
                    <span>Components:</span>
                    <span>{strategy?.components?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Connections:</span>
                    <span>{strategy?.connections?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Last Modified:</span>
                    <span>{new Date()?.toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setShowSaveDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                loading={isSaving}
                disabled={!strategyName?.trim()}
                className="flex-1"
              >
                Save Strategy
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StrategyToolbar;